import {
  Route,
  BrowserRouter,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";
import AuthRoute from "./AuthRoute";
import { useEffect, Suspense, useState, lazy, useRef } from "react";
import { SidebarProvider } from "../context/SidebarContext";
import { SidebarStateProvider } from "../context/SidebarStateContext";
import ErrorBoundary from "../components/ErrorBoundary/ErrorBoundary";
import FrontendSkeleton from "../components/skeleton/Views/FrontendSkeleton";
import { WebsiteTemplateSuspenseFallback } from "../Screens/DashboardView/MasterTemplateEditor/TemplatePublicLoading";
import {
  fetchWebsiteTemplateBootstrapCached,
  markCreatorPublicHomeIntent,
  prefetchWebsiteTemplateBootstrap,
} from "../Screens/DashboardView/MasterTemplateEditor/websiteTemplateBootstrap";
import { prefetchProfileSetup } from "../Utils/customHooks/useLoadProfileSetup";
import {
  getCreatorSlugFromHost,
  isNativeCreatorSubdomainHost,
} from "../Utils/url/getCreatorSlugFromHost";
import {
  fetchCreatorSlugFromDomainMappings,
  isDomainMappingCacheFresh,
  readCachedDomainMappingSlug,
  writeCachedDomainMappingSlug,
  clearCachedDomainMappingSlug,
} from "../Utils/url/fetchCreatorSlugFromDomainMappings";
import {
  isLocalDevHost,
  isSpaPlatformOrPreviewHost,
  isVercelDeploymentHost,
} from "../Utils/url/creatorHost";
import {
  getTopLevelPathSegment,
  isCreatorHostPublicPath,
  isReservedAppPath,
  RESERVED_APP_TOP_LEVEL_SEGMENTS,
  shouldSpoofCreatorHostPath,
} from "../Utils/url/reservedAppPaths";
import { spoofCreatorHostPath } from "../Utils/url/spoofCreatorHostPath";
import { getCreatorSpaRelativePath } from "../Utils/url/getCreatorSpaRelativePath";
import {
  readCachedCreatorPublicSurface,
  writeCachedCreatorPublicSurface,
} from "../Utils/url/creatorPublicSurface";
import {
  CreatorPublicLayout,
  CreatorPublicLibHome,
  CreatorPublicLibNested,
  preloadCreatorPublicSurface,
} from "../Screens/CreatorPublicHome/CreatorPublicHomeRoute";
const HomePage = lazy(() => import("../Screens/HomePage/HomePage"));
const GlobalView = lazy(() => import("../Screens/GlobalView/GlobalView"));
const Login = lazy(() => import("../Screens/Auth/LoginAuth/Login"));
const LoginLive = lazy(() => import("../Screens/Auth/LoginLive/LoginLive"));
const Error404 = lazy(() => import("../ErrorPages/Error404"));
const Error401 = lazy(() => import("../ErrorPages/Error401"));
const Error400 = lazy(() => import("../ErrorPages/Error400"));
const NoUser = lazy(() => import("../ErrorPages/NoUser"));
const GoogleCallback = lazy(
  () => import("../components/GoogleCallback/GoogleCallback"),
);
const Privacy = lazy(() => import("../Utils/Privacy"));
const Terms = lazy(() => import("../Utils/Terms"));
const SubscribeTest = lazy(() => import("../Utils/SubscribeTest"));
const SubscribeWebsite = lazy(() => import("../Utils/SubscribeWebsite"));
const WaitlistPage = lazy(() => import("../Screens/Waitlist/WaitlistPage"));
const PaymentCanceled = lazy(() => import("../Utils/PaymentCanceled"));
const LoadingDummyPage = lazy(
  () => import("../Screens/LoadingDummyPage/LoadingDummyPage"),
);
const UpdatePassword = lazy(
  () => import("../Screens/Auth/ResetPassword/updatePassword"),
);
const OnboardingPage = lazy(
  () => import("../Screens/Auth/OnboardingFlow/OnboardingPage"),
);
const WebsiteOnboardingMajorPage = lazy(
  () =>
    import(
      "../Screens/Auth/OnboardingFlow/WebsiteTemplateOnboarding/WebsiteOnboardingMajorPage"
    ),
);
const WebsiteOnboardingFullPage = lazy(
  () =>
    import(
      "../Screens/Auth/OnboardingFlow/WebsiteTemplateOnboarding/WebsiteOnboardingFullPage"
    ),
);
const ClaimAccountPage = lazy(
  () => import("../Screens/Auth/ClaimAccountFlow/ClaimAccountPage"),
);
const TeamPaymentSuccess = lazy(
  () => import("../Screens/Payment/TeamPaymentSuccess"),
);
const ComponentEmbed = lazy(
  () => import("../Screens/EmbeddedView/ComponentEmbed"),
);
const AcceptInvitePage = lazy(
  () => import("../Screens/Auth/AcceptInvite/AcceptInvitePage"),
);
const OrderSuccess = lazy(() => import("../Screens/Order/OrderSuccess"));
const OrderCanceled = lazy(() => import("../Screens/Order/OrderCanceled"));
const CartView = lazy(() => import("../components/Cart/CartView"));
const AdminLogin = lazy(
  () => import("../Screens/Auth/LoginProviders/AdminLogin"),
);
const EmbedViewHtml = lazy(
  () => import("../Screens/EmbeddedView/EmbedViewHtml"),
);
const DigitalProductCheckout = lazy(
  () => import("../Screens/DashboardView/Store/DigitalProductCheckout"),
);
const PaymentSuccess = lazy(
  () => import("../components/Checkout/PaymentSuccess"),
);
const OnboardingSuccess = lazy(
  () => import("../components/Seller/OnboardingSuccess"),
);
const StripeReturn = lazy(() => import("../components/Seller/StripeReturn"));
const StripeOnboardingRefresh = lazy(
  () => import("../components/Seller/StripeOnboardingRefresh"),
);
const PaymentCancel = lazy(
  () => import("../Screens/DashboardView/Account/AccountSection/PaymentCancel"),
);
const MasterclassOffer = lazy(
  () => import("../Screens/MasterclassOffer/MasterclassOffer"),
);
const CarolineAndJohn = lazy(
  () => import("../Screens/CarolineAndJohn/CarolineAndJohn"),
);
const DownloadApp = lazy(() => import("../Screens/DownloadApp/DownloadApp"));
const DownloadAdmin = lazy(
  () => import("../Screens/DownloadApp/DownloadAdmin"),
);
const CreatorPagesView = lazy(
  () => import("../features/creator-pages/CreatorPagesView"),
);

const CheckoutView = lazy(() => import("../components/Checkout/CheckoutView"));

const WebsiteTemplatePage = lazy(
  () => import("../Screens/WebsiteTemplatePage/WebsiteTemplatePage"),
);

// Lazy-loaded so the entire dashboard subtree (all tabs + antd/mui/charts) is code-split
// into its own chunk instead of shipping in `main.js`. This is the biggest lever for the
// public `/website/:username` initial load, which otherwise had to download the full
// dashboard bundle before rendering. Suspense fallback is null (no pre-dashboard skeleton).
const Dashboard = lazy(
  () => import("../Screens/DashboardView/Dashboard/Dashboard"),
);

// Public creator/embed screens — code-split out of `main.js`. Every usage below is
// already wrapped in a `Suspense` with a `FrontendSkeleton` fallback, so these load
// on demand instead of bloating the shared initial bundle.
const FollowerViewNew = lazy(() => import("../Screens/FollowerViewNew"));
const ProductCollectionView = lazy(
  () => import("../Screens/ProductCollectionView"),
);
const EmbeddedView = lazy(() => import("../Screens/EmbeddedView/EmbeddedView"));
const LinkInBioStaticHtmlPage = lazy(
  () => import("../Screens/LinkInBioStaticHtmlPage"),
);

/**
 * Eagerly-loaded wrapper for the `/website/:username` route.
 *
 * Fires `prefetchWebsiteTemplateBootstrap` synchronously during render — before
 * React starts downloading the lazy `WebsiteTemplatePage` chunk.  By the time the
 * chunk lands and `MasterTemplateContentProvider` runs its useQuery, the bootstrap
 * data is already in the react-query cache, so `isPublicBootstrapLoading` is false
 * and the page renders without any intermediate loading state.
 */
function WebsiteTemplateRouteWrapper() {
  const { username } = useParams<{ username?: string }>();
  const lastPrefetchedRef = useRef<string | undefined>(undefined);

  // Run synchronously during render (before Suspense starts the lazy import) so
  // the network request races the chunk download instead of waiting for it.
  // prefetchWebsiteTemplateBootstrap deduplicates in-flight requests, so calling
  // it more than once per username is safe.
  const raw = username?.trim();
  if (raw && raw !== lastPrefetchedRef.current) {
    lastPrefetchedRef.current = raw;
    try {
      prefetchWebsiteTemplateBootstrap(decodeURIComponent(raw));
      if (/\/link-in-bio(?:\/|$)/i.test(window.location.pathname)) {
        prefetchProfileSetup(decodeURIComponent(raw));
      }
    } catch {
      prefetchWebsiteTemplateBootstrap(raw);
      if (/\/link-in-bio(?:\/|$)/i.test(window.location.pathname)) {
        prefetchProfileSetup(raw);
      }
    }
  }

  return (
    <ErrorBoundary>
      <Suspense
        fallback={<WebsiteTemplateSuspenseFallback username={raw} />}
      >
        <WebsiteTemplatePage />
      </Suspense>
    </ErrorBoundary>
  );
}

function isWebsiteTemplatePath(pathname: string): boolean {
  return /^\/website\/[^/]+/i.test(pathname || "");
}

/**
 * Custom domains (not *.browsable.ai) need an async `domain_mappings` lookup
 * unless we already have a cached slug for first paint.
 */
function needsCustomDomainSlugLookup(): boolean {
  try {
    const hostname = window.location.hostname;
    const pathname = window.location.pathname || "/";
    if (isWebsiteTemplatePath(pathname)) return false;
    if (isSpaPlatformOrPreviewHost(hostname)) return false;
    if (isNativeCreatorSubdomainHost(hostname)) return false;
    if (!shouldSpoofCreatorHostPath(pathname)) return false;
    if (readCachedDomainMappingSlug(hostname)) return false;
    return true;
  } catch {
    return false;
  }
}

function LegacyCreatorPageRedirect() {
  const { pageSlug, username } = useParams<{
    pageSlug: string;
    username: string;
  }>();

  if (!username?.trim() || !pageSlug?.trim()) {
    return <Navigate to="/" replace />;
  }

  return (
    <Navigate
      to={`/${encodeURIComponent(username)}/page/${encodeURIComponent(pageSlug)}`}
      replace
    />
  );
}

const Routing = () => {
  const actualLocation = useLocation();
  const navigate = useNavigate();
  // Synchronously derive the slug for native Browsable subdomains (*.browsable.bio,
  // *.browsable.ai), hard-mapped custom domains, and localStorage-cached mappings
  // so `spoofedLocation` is correct on the very first render and the marketing
  // home page never flashes before the useEffect fires.
  const [creatorSlug, setCreatorSlug] = useState<string | undefined>(() => {
    try {
      const hostname = window.location.hostname;
      const fromHost = getCreatorSlugFromHost(hostname);
      if (fromHost) return fromHost;
      if (isSpaPlatformOrPreviewHost(hostname)) return undefined;
      return readCachedDomainMappingSlug(hostname);
    } catch {
      return undefined;
    }
  });
  /** null = still resolving; drives Website vs LiB on creator hosts. */
  const [creatorUsesWebsite, setCreatorUsesWebsite] = useState<boolean | null>(
    () => {
      try {
        const hostname = window.location.hostname;
        const slug =
          getCreatorSlugFromHost(hostname) ||
          (!isSpaPlatformOrPreviewHost(hostname)
            ? readCachedDomainMappingSlug(hostname)
            : undefined);
        if (!slug) return null;
        const cached = readCachedCreatorPublicSurface(slug);
        if (cached === "website") return true;
        if (cached === "link-in-bio") return false;
      } catch {
        // ignore
      }
      return null;
    },
  );
  // Start resolving on first paint for uncached custom domains — otherwise `/`
  // mounts HomePage for at least one frame (and up to the old 150ms grace).
  const [isResolvingCreatorSlug, setIsResolvingCreatorSlug] = useState(
    () => needsCustomDomainSlugLookup(),
  );
  const shouldShowHostResolutionSkeleton =
    isResolvingCreatorSlug &&
    !creatorSlug &&
    !isLocalDevHost(window.location.hostname) &&
    !isReservedAppPath(actualLocation.pathname);

  // Supabase password recovery links send #access_token=...&type=recovery in the URL hash.
  // If the token lands on any page other than /auth/update-password (e.g. the root because
  // the Supabase redirect_to is not allowlisted), redirect smoothly via React Router so
  // there is no hard page reload. Supabase's _initialize() captures the token synchronously
  // at startup so the session is established in the background regardless of navigation.
  useEffect(() => {
    const rawHash = window.location.hash.replace(/^#/, "");
    if (!rawHash) return;
    const params = new URLSearchParams(rawHash);
    if (params.get("type") === "recovery" && params.get("access_token")) {
      if (!window.location.pathname.includes("/auth/update-password")) {
        // Client-side navigation — preserve the hash so the UpdatePassword screen
        // can reliably install the recovery tokens (and avoid "Auth session missing").
        navigate(`/auth/update-password#${rawHash}`, { replace: true });
      }
    }
  }, [navigate]);

  // Resolve creator slug for custom domains via `domain_mappings`.
  // Fresh TTL cache skips the network (Slow 4G: remapping on every SPA nav
  // was stacking RTT). Stale cache still revalidates once per host session.
  useEffect(() => {
    const pathname = actualLocation.pathname || "/";
    if (isWebsiteTemplatePath(pathname)) {
      setIsResolvingCreatorSlug(false);
      return;
    }

    const hostname = window.location.hostname;
    if (isSpaPlatformOrPreviewHost(hostname)) {
      if (isVercelDeploymentHost(hostname)) {
        clearCachedDomainMappingSlug(hostname);
      }
      setIsResolvingCreatorSlug(false);
      return;
    }

    if (isNativeCreatorSubdomainHost(hostname)) {
      const slugFromHost = getCreatorSlugFromHost(hostname);
      if (slugFromHost) setCreatorSlug(slugFromHost);
      setIsResolvingCreatorSlug(false);
      return;
    }

    if (!shouldSpoofCreatorHostPath(pathname)) {
      setIsResolvingCreatorSlug(false);
      return;
    }

    const host = String(hostname || "").toLowerCase();
    const cachedSlug = readCachedDomainMappingSlug(host);
    if (cachedSlug) {
      setCreatorSlug(cachedSlug);
      setIsResolvingCreatorSlug(false);
      // Fresh hit — no network. Stale — background revalidate without blocking paint.
      if (isDomainMappingCacheFresh(host)) return;
    }

    let cancelled = false;
    if (!cachedSlug) setIsResolvingCreatorSlug(true);

    (async () => {
      try {
        const slug = await fetchCreatorSlugFromDomainMappings(host, {
          force: Boolean(cachedSlug),
        });
        if (cancelled) return;
        if (slug) {
          setCreatorSlug(slug);
          writeCachedDomainMappingSlug(host, slug);
        }
      } catch (_e) {
        // non-blocking: if we can't resolve, keep default routing.
      } finally {
        if (!cancelled) setIsResolvingCreatorSlug(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Path changes are fine: fresh TTL cache returns before any network call.
  }, [actualLocation.pathname]);

  // Resolve Website vs Link-in-Bio for creator hosts so spoof can elevate to `/website/:slug`.
  useEffect(() => {
    const slug = String(creatorSlug || "").trim().toLowerCase();
    if (!slug) {
      setCreatorUsesWebsite(null);
      return;
    }
    if (isWebsiteTemplatePath(actualLocation.pathname || "")) return;

    const cached = readCachedCreatorPublicSurface(slug);
    if (cached === "website") {
      setCreatorUsesWebsite(true);
    } else if (cached === "link-in-bio") {
      setCreatorUsesWebsite(false);
    } else {
      setCreatorUsesWebsite(null);
    }

    const isCreatorHostHome = (actualLocation.pathname || "/").replace(/\/+$/, "") === "";
    if (isCreatorHostHome) {
      markCreatorPublicHomeIntent(slug);
      preloadCreatorPublicSurface(slug, cached ?? "website");
    }

    let cancelled = false;
    // Single RQ-backed flight (prefetch + await share the same cache key).
    prefetchWebsiteTemplateBootstrap(slug);
    (async () => {
      try {
        const result = await fetchWebsiteTemplateBootstrapCached(slug);
        if (cancelled) return;
        if (result.userNotFound) {
          setCreatorUsesWebsite(false);
          writeCachedCreatorPublicSurface(slug, "link-in-bio");
          return;
        }
        const usesWebsite = Boolean(result.hasWebsiteSurface);
        setCreatorUsesWebsite(usesWebsite);
        writeCachedCreatorPublicSurface(
          slug,
          usesWebsite ? "website" : "link-in-bio",
        );
      } catch {
        if (!cancelled) setCreatorUsesWebsite(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [creatorSlug, actualLocation.pathname]);

  // Hard stop on Vercel only: don't follow browsable.ai/bio anchors off-host.
  // Production / custom-domain hosts are unaffected (effect returns immediately).
  useEffect(() => {
    if (!isVercelDeploymentHost()) return;

    const blockOffHostNavigation = (event: MouseEvent) => {
      const target = event.target as Element | null;
      const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      // Don't hijack modified clicks (new tab / download).
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      if (anchor.target && anchor.target !== "_self") return;
      const href = anchor.getAttribute("href") || "";
      if (!/^https?:\/\//i.test(href)) return;
      try {
        const url = new URL(href, window.location.origin);
        if (isVercelDeploymentHost(url.hostname)) return;
        if (
          !(
            url.hostname === "browsable.ai" ||
            url.hostname === "www.browsable.ai" ||
            url.hostname === "browsable.bio" ||
            url.hostname === "www.browsable.bio" ||
            url.hostname.endsWith(".browsable.ai") ||
            url.hostname.endsWith(".browsable.bio") ||
            url.hostname === "searchbubble.com" ||
            url.hostname === "www.searchbubble.com"
          )
        ) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        const next = `${url.pathname}${url.search}${url.hash}` || "/";
        navigate(next);
      } catch (_e) {
        // ignore
      }
    };

    document.addEventListener("click", blockOffHostNavigation, true);
    return () =>
      document.removeEventListener("click", blockOffHostNavigation, true);
  }, [navigate]);

  // Redirect browsable.bio -> browsable.ai for homepage + app routes.
  // (Dashboard and marketing pages should always live on browsable.ai; only creator public pages
  // can use browsable.bio depending on preference.)
  useEffect(() => {
    const host = window.location.hostname;
    const isBioRoot = host === "browsable.bio" || host === "www.browsable.bio";
    if (!isBioRoot) return;

    const pathname = actualLocation.pathname || "/";
    // Public creator routes on platform domains should honor creator preference
    // (browsable.ai vs browsable.bio) and must not be force-redirected.
    const isCreatorScopedPublicRoute = /^\/(preview|embed|embeddemo|website)\/[^/]+/i.test(
      pathname
    );
    if (isCreatorScopedPublicRoute) return;
    const top = getTopLevelPathSegment(pathname);

    if (!RESERVED_APP_TOP_LEVEL_SEGMENTS.has(top)) return;

    const target = `https://browsable.ai${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.replace(target);
  }, [actualLocation.pathname, actualLocation.search, actualLocation.hash]);

  const spoofedLocation =
    creatorSlug && shouldSpoofCreatorHostPath(actualLocation.pathname)
      ? {
          ...actualLocation,
          pathname: spoofCreatorHostPath(
            actualLocation.pathname,
            creatorSlug,
          ),
        }
      : actualLocation;

  // Avoid flashing LiB while we learn the creator has a website on mapped hosts.
  // Embed / preview are independent of Website vs LiB — never block them on
  // website-surface bootstrap (master-template merge regression).
  const waitingForCreatorWebsiteSurface =
    Boolean(creatorSlug) &&
    creatorUsesWebsite === null &&
    shouldSpoofCreatorHostPath(actualLocation.pathname) &&
    !isWebsiteTemplatePath(actualLocation.pathname || "") &&
    !isCreatorHostPublicPath(actualLocation.pathname || "");

  function PrivateBetaRedirect() {
    useEffect(() => {
      window.location.href =
        "https://calendly.com/searchbubble/private-beta-onboarding?month=2024-09";
    }, []);

    return null;
  }

  return (
    <SidebarStateProvider>
      <SidebarProvider>
        {shouldShowHostResolutionSkeleton || waitingForCreatorWebsiteSurface ? (
          <div
            role="status"
            aria-busy="true"
            aria-label="Loading"
            style={{ minHeight: "100dvh", background: "#fff" }}
          />
        ) : (
          <Suspense
            fallback={
              <div
                role="status"
                aria-busy="true"
                aria-label="Loading"
                style={{ minHeight: "100dvh", background: "#fff" }}
              />
            }
          >
          <Routes location={spoofedLocation}>
            <Route path="/" element={<AuthRoute Component={HomePage} />} />
            <Route
              path="/login"
              element={
                <ErrorBoundary>
                  <AuthRoute Component={Login} />
                </ErrorBoundary>
              }
            />

            <Route
              path="/login-live"
              element={
                <ErrorBoundary>
                  <AuthRoute Component={LoginLive} />
                </ErrorBoundary>
              }
            />
            <Route
              path="/onboarding"
              element={
                <ErrorBoundary>
                  <AuthRoute Component={OnboardingPage} />
                </ErrorBoundary>
              }
            />
            <Route
              path="/website-onboarding"
              element={
                <ErrorBoundary>
                  {/* Default / Stripe success path → major question bank */}
                  <AuthRoute Component={WebsiteOnboardingMajorPage} />
                </ErrorBoundary>
              }
            />
            <Route
              path="/website-onboarding-major"
              element={
                <ErrorBoundary>
                  <AuthRoute Component={WebsiteOnboardingMajorPage} />
                </ErrorBoundary>
              }
            />
            <Route
              path="/website-onboarding-full"
              element={
                <ErrorBoundary>
                  <AuthRoute Component={WebsiteOnboardingFullPage} />
                </ErrorBoundary>
              }
            />
            <Route
              path="/claim"
              element={
                <ErrorBoundary>
                  <ClaimAccountPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/claim/:username"
              element={
                <ErrorBoundary>
                  <ClaimAccountPage />
                </ErrorBoundary>
              }
            />
            {/* <Route 
            path="/tutorials" 
            element={
              <ErrorBoundary>
                <Tutorials />
              </ErrorBoundary>
            } 
          /> */}
            <Route
              path="/googleCallback"
              element={
                <ErrorBoundary>
                  <GoogleCallback />
                </ErrorBoundary>
              }
            />
            <Route
              path="/auth/update-password"
              element={
                <ErrorBoundary>
                  <UpdatePassword />
                </ErrorBoundary>
              }
            />

            <Route
              path="/login-admin"
              element={
                <ErrorBoundary>
                  <AdminLogin />
                </ErrorBoundary>
              }
            />
            <Route path="/subscribe" element={<SubscribeTest />} />
            <Route path="/pricing" element={<SubscribeTest />} />
            <Route path="/subscribe-website" element={<SubscribeWebsite />} />
            <Route path="/waitlist" element={<WaitlistPage />} />

            <Route path="/payment-canceled" element={<PaymentCanceled />} />
            <Route
              path="/add-team-payment-success"
              element={<TeamPaymentSuccess />}
            />

            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route
              path="/loading/:username"
              element={
                <ErrorBoundary>
                  <ProtectedRoute Component={LoadingDummyPage} />
                </ErrorBoundary>
              }
            />

            <Route
              path="/dashboard/*"
              element={
                <ErrorBoundary>
                  <Suspense fallback={null}>
                    <ProtectedRoute Component={Dashboard} />
                  </Suspense>
                </ErrorBoundary>
              }
            />

            <Route path="/website/:username/*" element={<WebsiteTemplateRouteWrapper />} />

            <Route path="/preview/:username">
              <Route
                index
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<FrontendSkeleton />}>
                      <FollowerViewNew />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="contact-me"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<FrontendSkeleton />}>
                      <FollowerViewNew />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="shop/collections/:collectionId"
                element={
                  <ErrorBoundary>
                    <WebsiteTemplateRouteWrapper />
                  </ErrorBoundary>
                }
              />
              <Route
                path="collections/:collectionId"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<FrontendSkeleton />}>
                      <ProductCollectionView />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path=":id"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<FrontendSkeleton />}>
                      <FollowerViewNew />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
            </Route>
            <Route path="/embed/:username">
              <Route
                index
                element={
                  <ErrorBoundary>
                    <Suspense fallback={null}>
                      <EmbeddedView />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="contact-me"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={null}>
                      <EmbeddedView />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path=":id"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={null}>
                      <EmbeddedView />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              {/* Dynamic component embed route */}
              <Route
                path="component/:id"
                element={
                  <ErrorBoundary>
                    <ComponentEmbed />
                  </ErrorBoundary>
                }
              />
            </Route>
            {/* <Route path="/pages/:username">
            <Route
              index
              element={
                <ErrorBoundary>
                  <Suspense fallback={<FrontendSkeleton />}>
                    <CreatorPagesView />
                  </Suspense>
                </ErrorBoundary>
              }
            />
          </Route> */}
            <Route
              path="/pages/:pageSlug/:username"
              element={<LegacyCreatorPageRedirect />}
            />
            {/* <Route path="/pages/:username/:pageSlug">
            <Route
              index
              element={
                <ErrorBoundary>
                  <Suspense fallback={<FrontendSkeleton />}>
                    <CreatorPagesView />
                  </Suspense>
                </ErrorBoundary>
              }
            />
          </Route> */}

            {/* Hard-coded marketing pages; must not apply to other usernames */}
            <Route
              path="/therealdba/linkinbio"
              element={
                <ErrorBoundary>
                  <Suspense fallback={<FrontendSkeleton />}>
                    <LinkInBioStaticHtmlPage />
                  </Suspense>
                </ErrorBoundary>
              }
            />
            <Route
              path="/beverlyhillspediatrics/linkinbio"
              element={
                <ErrorBoundary>
                  <Suspense fallback={<FrontendSkeleton />}>
                    <LinkInBioStaticHtmlPage src="/linkinbio/beverlyhillspediatrics.html" />
                  </Suspense>
                </ErrorBoundary>
              }
            />

            {/*
              Shared layout keeps WebsiteTemplatePage mounted across
              /{username} → /{username}/shop|content|search|link-in-bio so
              nested pages update path rest in place (no full remount/loader).
              LiB creators render children via Outlet.
            */}
            <Route
              path="/:username"
              element={
                <ErrorBoundary>
                  <CreatorPublicLayout />
                </ErrorBoundary>
              }
            >
              <Route index element={<CreatorPublicLibHome />} />
              <Route
                path="contact-me"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<FrontendSkeleton />}>
                      <FollowerViewNew />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="page/:pageSlug"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<FrontendSkeleton />}>
                      <CreatorPagesView />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="shop/collections/:collectionId"
                element={
                  <ErrorBoundary>
                    <WebsiteTemplateRouteWrapper />
                  </ErrorBoundary>
                }
              />
              <Route
                path="collections/:collectionId"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<FrontendSkeleton />}>
                      <ProductCollectionView />
                    </Suspense>
                  </ErrorBoundary>
                }
              />

              <Route path=":pageSlug/*" element={<CreatorPublicLibNested />} />
            </Route>
            {/* Non-embed component route */}
            <Route
              path="/:username/component/:id"
              element={<ComponentEmbed />}
            />

            {/* <Route path="/embed/:username" element={<EmbeddedView />} /> */}
            <Route path="/embeddemo/:username" element={<EmbedViewHtml />} />

            <Route
              path="/:username/component/:id"
              element={<ComponentEmbed />}
            />

            {/* New seller routes */}
            {/* <Route path="/seller/onboarding" element={<SellerOnboarding />} />
          <Route
            path="/seller/dashboard"
            element={<SellerRoute component={SellerDashboard} />}
          />
          <Route
            path="/seller-product/:productId"
            element={<SellerProductDetail />}
          /> */}

            {/* Order routes */}
            <Route path="/order/success" element={<OrderSuccess />} />
            <Route path="/order/canceled" element={<OrderCanceled />} />
            <Route
              path="/checkout/:productId"
              element={
                <Suspense fallback={<FrontendSkeleton />}>
                  <CheckoutView />
                </Suspense>
              }
            />
            <Route
              path="/product-checkout/:productId"
              element={
                <ErrorBoundary>
                  <DigitalProductCheckout />
                </ErrorBoundary>
              }
            />

            {/* Cart route */}
            <Route path="/cart" element={<CartView />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/stripe/return" element={<StripeReturn />} />
            <Route
              path="/stripe/onboarding"
              element={<StripeOnboardingRefresh />}
            />
            <Route path="/onboarding-success" element={<OnboardingSuccess />} />
            <Route path="/payment-cancel" element={<PaymentCancel />} />

            <Route path="/global" element={<GlobalView />} />
            <Route path="/private-beta" element={<PrivateBetaRedirect />} />
            <Route
              path="/global?term=:queryTerm&from=:searchFrom"
              element={<GlobalView />}
            />
            <Route path="/no-user-found" element={<NoUser />} />
            <Route path="/404" element={<Error404 />} />
            <Route path="/401" element={<Error401 />} />
            <Route path="/masterclass" element={<MasterclassOffer />} />
            <Route path="/caroline-and-john" element={<CarolineAndJohn />} />
            <Route path="/download" element={<DownloadApp />} />
            <Route path="/download-admin" element={<DownloadAdmin />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/*" element={<Error400 />} />
          </Routes>
          </Suspense>
        )}
      </SidebarProvider>
    </SidebarStateProvider>
  );
};

export default Routing;
